/**
 * Winter27 Collection · Pre-order (sections/selection-room.liquid; inside the code it is still
 * "the Selection Room"): every screen of the private page, drawn from the App Developer's API
 * (docs/selection-room/API.md in the app repo).
 *
 * Since 24 Oct (Ibrahim's "A"): no member code and no phone at the door. The first screen (the
 * campaign photo when the dashboard has one, the three steps, "Explore Winter27") → the pieces
 * ⇄ a piece (Reserve my size, Like, "Not for me") → "My list" (the done screen). He likes and
 * reserves first; his picks wait in this browser (localStorage kz_room_picks) until he presses
 * "Save my reserves" on My list: his phone, the 6-digit WhatsApp code, and everything is saved
 * at once (POST /save). Signed in (localStorage kz_room_token), every change is saved straight
 * away. After closing, the results. Also: closed, not open yet, nothing here.
 * The pieces, a piece and My list have their own place in the history (#pieces, #piece=… and
 * #done), so the back button goes where he expects.
 *
 * The pieces, since 24 Oct evening (Ibrahim, board KZ-20261024-w27-reel-feed; the same as the
 * app): one by one, like Reels, is the first view. One piece fills the screen, its photos whole
 * (never cropped) and swiped sideways (arrows with a mouse), with ♥ / Reserve my size (a sheet
 * with colour and size) / ✕ Not for me (a sheet with the reason) under them. "Grid" shows the
 * cards instead; the choice lasts for the visit (sessionStorage kz_room_view). Every piece seen is counted
 * with how long it was looked at (Piece Impression), and every event about a piece says how it
 * was being looked at (view_mode reel | cards | detail) and where it was in the list (position).
 *
 * The API answers a fixed word for every refusal; the words he reads are the theme's, in the
 * page's language, and every refusal is shown on screen, never only read out. No counts of
 * other people are ever shown. The phone number never goes to Mixpanel.
 */
(() => {
  const TOKEN = 'kz_room_token';
  const PICKS = 'kz_room_picks';
  const VIEW = 'kz_room_view';
  // The colour picked for each piece, kept for this visit, so it stays when he opens the piece, comes back or reloads.
  const COLORS = 'kz_room_colors';
  // The arrow that points at the view switch: shown on his first visits until he has used the switch.
  const HINT = 'kz_room_view_hint';
  // A piece counts as seen once this much of it is on screen; shorter than DWELL is a scroll-past (the app's numbers).
  const SEEN = { reel: 0.6, cards: 0.5, rail: 0.5 };
  const DWELL = 400;
  const CODES = [['965', 8], ['966', 9], ['971', 9], ['974', 8], ['973', 8], ['968', 8]];
  const REASONS = ['price', 'colour', 'fit', 'style'];
  // The KANZE shades of the Winter27 colour names, the app's own table (shared/swatch-color.ts
  // KANZE_SHADES, 25 Oct): reading these studio photos gave near-black for Grey, Navy and Olive.
  // The shop's own swatch, when the server sends one, comes first; a name not in the table is
  // read from its photo.
  const SHADES = {
    black: '#1a1a1a', brown: '#5e4334', navy: '#1b2440', grey: '#7a7b7e', 'light grey': '#c6c6c4',
    khaki: '#8a7d55', beige: '#d6c7ae', stone: '#b9ae9b', ecru: '#efe9dc', camel: '#b98a5a',
    mink: '#8c7b6e', smoke: '#6e6e6c', 'snow melange': '#dcdcd9', bone: '#e3dccd', olive: '#5d5a3a',
    green: '#3f6e56', burgundy: '#5a1f2c', 'petrol blue': '#1f5566', 'baby blue': '#a9c4de', 'saxe blue': '#4a78a8',
  };

  const esc = (text) => String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const kept = (run, otherwise = null) => { try { return run(); } catch { return otherwise; } };
  const track = (name, props, how) => window.kz?.track?.(name, props, how);
  const empty = () => ({ reserves: [], likes: [], reasons: {}, likeColors: {} });

  /** The number in E.164 for that country code, or '' (the same reading as Notify me). */
  function e164(code, typed) {
    const length = (CODES.find(([c]) => c === code) || [])[1];
    let local = String(typed || '').replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)).replace(/\D/g, '');
    if (!length) return '';
    if (local.startsWith('00' + code)) local = local.slice(code.length + 2);
    else if (local.length > length && local.startsWith(code)) local = local.slice(code.length);
    if (local.length === length + 1 && local.startsWith('0')) local = local.slice(1);
    return local.length === length && !local.startsWith('0') ? `+${code}${local}` : '';
  }

  class SelectionRoom extends HTMLElement {
    connectedCallback() {
      // Shopify hands translations over HTML-escaped ("Ibrahim &amp; Ali"); they are read back to plain text once here.
      const plain = document.createElement('textarea');
      const words = kept(() => JSON.parse(this.querySelector('[data-room-words]').textContent), {});
      this.words = Object.fromEntries(Object.entries(words).map(([key, text]) => { plain.innerHTML = text; return [key, plain.value]; }));
      this.api = (this.dataset.api || '').replace(/\/+$/, '');
      this.ar = (document.documentElement.lang || '').startsWith('ar');
      this.view = this.querySelector('[data-room-view]');
      this.live = this.querySelector('[data-room-live]');
      this.token = kept(() => localStorage.getItem(TOKEN), null);
      this.choice = {};
      this.filter = 'all';
      this.entered = false;
      // A refusal with no line of its own on the screen (a heart on the pieces, the WhatsApp switch) shows here.
      this.flashLine = document.createElement('p');
      this.flashLine.className = 'room__flash';
      this.flashLine.setAttribute('role', 'alert');
      this.flashLine.hidden = true;
      this.append(this.flashLine);
      // One by one (like Reels) first; the grid when he chose it before.
      // Always the cards first on a new visit (Ibrahim, 25 Oct 1:25 am); one by one only for the rest
      // of a visit in which he chose it. The old remembered choice is forgotten.
      kept(() => localStorage.removeItem(VIEW));
      this.mode = kept(() => sessionStorage.getItem(VIEW)) === 'reel' ? 'reel' : 'cards';
      // The home row: the same cards, from the same server, in a row (sections/room-rail.liquid).
      this.rail = this.dataset.mode === 'rail';
      if (this.rail) this.mode = 'rail';
      this.colors = kept(() => JSON.parse(sessionStorage.getItem(COLORS) || '{}'), {}) || {};
      const hint = kept(() => JSON.parse(localStorage.getItem(HINT) || '{}'), {}) || {};
      this.hint = !this.rail && !hint.used && (hint.visits || 0) < 3;
      if (this.hint) kept(() => localStorage.setItem(HINT, JSON.stringify({ visits: (hint.visits || 0) + 1 })));
      // Photos near the screen: every colour's first photo is fetched ahead, so a new colour shows at once.
      this.ahead = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) { this.ahead.unobserve(entry.target); this.preload(entry.target); } }), { rootMargin: '600px' }) : null;
      // The colour dots use the app's own colour-name rules (assets/swatch-color.js), loaded beside the page.
      this.swatches = this.dataset.swatchSrc ? import(this.dataset.swatchSrc).catch(() => null) : Promise.resolve(null);
      this.shown = [];
      // The sheets (Reserve your size, Not for me) open over the pieces as a modal dialog.
      this.sheet = document.createElement('dialog');
      this.sheet.className = 'room__sheet';
      this.sheet.setAttribute('aria-labelledby', 'RoomSheetTitle');
      this.sheet.addEventListener('click', (event) => { if (event.target === this.sheet) this.sheet.close(); });
      this.sheet.addEventListener('close', () => {
        if (!this.refreshOnClose) return;
        this.refreshOnClose = false;
        this.route(false);
      });
      this.append(this.sheet);
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('change', (event) => this.onChange(event));
      // Photo swipes: a scroll does not bubble, so it is caught on the way down.
      this.addEventListener('scroll', (event) => this.onScroll(event), true);
      this.addEventListener('touchstart', (event) => this.onTouch(event), { passive: false });
      // Every piece of the feed has the same photo box (Ibrahim, 29 Oct): sized when drawn and again when the screen changes size.
      this.addEventListener('load', (event) => {
        if (event.target.matches?.('.room__reel-photo:first-child img')) event.target.closest('.room__reel-media')?.classList.add('is-loaded');
        // The shade under a photo box is only for while the photo is on its way.
        if (event.target.matches?.('.room__card-photo:first-child img, .room__photo:first-child img')) event.target.closest('.room__card-media, .room__gallery')?.classList.add('is-loaded');
      }, true);
      this.resizer = new ResizeObserver(() => this.fit());
      // A piece on screen while he leaves the tab is counted then, and its time starts again when he comes back.
      document.addEventListener('visibilitychange', () => this.onVisibility());
      this.depth = 0;
      window.addEventListener('popstate', () => {
        // The photo viewer's own step of history (open, then Back to close) is not a move between screens.
        if (this.viewing || this.rail) return;
        this.depth = Math.max(0, this.depth - 1);
        if (this.me) this.route();
      });
      this.start();
    }

    // ─── Words, numbers and dates ────────────────────────────────────────────

    /** A word of the language files, with its %name% places filled (the section writes them so: real Liquid refuses a brace there). */
    t(key, values = {}) {
      return (this.words[key] || '').replace(/%(\w+)%/g, (all, name) => (name in values ? values[name] : all));
    }

    num(value) {
      return this.ar ? String(value).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[d]) : String(value);
    }

    /** A time from the API (UTC) in Kuwait time: "Sat 7 Nov, 11 pm". */
    date(iso) {
      const at = new Date(iso);
      if (!iso || Number.isNaN(at.getTime())) return '';
      const minutes = Number(new Intl.DateTimeFormat('en', { timeZone: 'Asia/Kuwait', minute: 'numeric' }).format(at));
      const locale = this.ar ? 'ar-KW-u-nu-arab' : 'en-GB';
      const day = new Intl.DateTimeFormat(locale, { timeZone: 'Asia/Kuwait', weekday: 'short', day: 'numeric', month: 'short' }).format(at);
      const time = new Intl.DateTimeFormat(locale, { timeZone: 'Asia/Kuwait', hour: 'numeric', ...(minutes ? { minute: '2-digit' } : {}), hour12: true }).format(at);
      return `${day.replace(',', '')}${this.ar ? '، ' : ', '}${time}`;
    }

    /** "+965 5xxx xx34": enough to know it is his number, not the whole of it. */
    masked(phone) {
      const code = (CODES.find(([c]) => phone.startsWith('+' + c)) || [''])[0];
      const local = phone.slice(code.length + 1);
      const hidden = local.slice(0, 1) + 'x'.repeat(Math.max(0, local.length - 3)) + local.slice(-2);
      return `+${code} ${hidden.slice(0, 4)} ${hidden.slice(4)}`;
    }

    say(text) {
      if (!this.live) return;
      this.live.textContent = '';
      window.setTimeout(() => { this.live.textContent = text; }, 50);
    }

    // ─── The API ─────────────────────────────────────────────────────────────

    async call(path, body) {
      const headers = {};
      if (body) headers['Content-Type'] = 'application/json';
      if (this.token) headers.Authorization = `Bearer ${this.token}`;
      let response;
      try {
        response = await fetch(`${this.api}${path}`, { method: body ? 'POST' : 'GET', headers, body: body ? JSON.stringify(body) : undefined });
      } catch {
        return { ok: false, reason: 'network' };
      }
      const data = await response.json().catch(() => ({}));
      if (response.status === 401 || data.reason === 'signed_out') {
        this.forget();
        return { ok: false, reason: 'signed_out' };
      }
      return { ...data, ok: response.ok && data.ok !== false, reason: data.reason || (response.ok ? '' : response.status >= 500 || response.status === 404 ? 'unavailable' : 'invalid') };
    }

    forget() {
      this.token = null;
      this.me = null;
      kept(() => localStorage.removeItem(TOKEN));
    }

    // ─── His picks before he saves them ──────────────────────────────────────

    picks() {
      const saved = kept(() => JSON.parse(localStorage.getItem(PICKS) || 'null'), null);
      return saved && Array.isArray(saved.reserves) && Array.isArray(saved.likes) ? { reserves: saved.reserves, likes: saved.likes, reasons: saved.reasons || {}, likeColors: saved.likeColors || {} } : empty();
    }

    keepPicks(mine) {
      kept(() => localStorage.setItem(PICKS, JSON.stringify(mine)));
    }

    hasPicks(mine = this.picks()) {
      return Boolean(mine.reserves.length || mine.likes.length || Object.keys(mine.reasons).length);
    }

    /** Reserving, liking and "not for me" are on only while both switches (here and in the dashboard) are. */
    get requestsOpen() {
      return this.dataset.requestsOpen !== 'false' && this.me?.requestsOpen !== false && this.me?.state === 'open';
    }

    // ─── The flow ────────────────────────────────────────────────────────────

    async start() {
      if (this.rail) return this.startRail();
      if (this.dataset.roomOpen === 'false') return this.closed();
      const [room, swatches] = await Promise.all([this.call('/room'), this.swatches]);
      this.swatchModule = swatches;
      if (!room.ok) return this.trouble(room.reason, () => this.start());
      this.room = room;
      if (room.state === 'off') return this.nothing();
      if (room.state === 'not_open') return this.notOpen(room.webOpensAt);
      if (this.token) return this.loadMe();
      if (room.state === 'closed') return this.closed();
      return this.loadPieces();
    }

    /**
     * The home row: only between its dates and while the pre-order is open; else it takes no room.
     * The pieces in the order chosen in the row's settings, then the room's order (the page shows
     * no one else's numbers), the first "limit" of them, then a card to see them all.
     */
    async startRail() {
      const now = Date.now();
      const from = Date.parse(this.dataset.from || '');
      const until = Date.parse(this.dataset.until || '');
      if ((from && now < from) || (until && now >= until)) return;
      let [answer, swatches] = await Promise.all([this.token ? this.call('/me') : this.call('/pieces'), this.swatches]);
      if (!answer.ok && answer.reason === 'signed_out') answer = await this.call('/pieces');
      this.swatchModule = swatches;
      if (!answer.ok || answer.state !== 'open' || !(answer.pieces || []).length) {
        this.hidden = true;
        return;
      }
      this.local = !answer.mine;
      this.me = { state: answer.state, requestsOpen: answer.requestsOpen, closesAt: answer.closesAt, pieces: answer.pieces, mine: answer.mine || this.picks(), consent: {}, results: null };
      this.drawRail();
      this.hidden = false;
      if (until && until - now < 2147483647) window.setTimeout(() => { this.hidden = true; }, until - now);
    }

    drawRail() {
      this.unwatch();
      const pieces = this.me.pieces;
      // A chosen order first (the row's "Pieces first" setting: numbers such as 07 or whole handles), then the room's order.
      const wanted = (this.dataset.order || '').split(/[\s,،]+/).filter(Boolean);
      const first = wanted.map((entry) => pieces.find((p) => p.handle === entry || (p.handle || '').includes(`-${entry}-`))).filter(Boolean);
      const ordered = [...new Set([...first, ...pieces])];
      const shown = ordered.slice(0, Number(this.dataset.limit) || 12);
      this.shown = shown.map((p) => p.id);
      const all = `<li class="rail__item room__rail-all"><a class="room__rail-all-link" href="${esc(this.dataset.pageUrl || '')}#pieces">${esc(this.t('room.see_all', { count: this.num(pieces.length) }))}</a></li>`;
      this.view.innerHTML = `<div class="rail"><ul class="rail__track room__rail" role="list" aria-label="${esc(this.t('eyebrow'))}">${shown.map((p) => this.card(p)).join('')}${pieces.length > shown.length ? all : ''}</ul></div>`;
      const cards = this.view.querySelectorAll('.room__card[data-piece]');
      this.watch(cards, null, 'rail');
      cards.forEach((card) => this.ahead?.observe(card));
    }

    /** Not signed in: the pieces for everyone, and his picks from this browser. */
    async loadPieces(focus = false) {
      const answer = await this.call('/pieces');
      if (!answer.ok) return this.trouble(answer.reason, () => this.loadPieces(focus));
      this.local = true;
      this.me = { state: answer.state, requestsOpen: answer.requestsOpen, closesAt: answer.closesAt, pieces: answer.pieces || [], mine: this.picks(), consent: {}, results: null };
      if (answer.state === 'closed') return this.closed();
      if (answer.state === 'not_open') return this.notOpen(this.room?.webOpensAt);
      if (answer.state === 'off') return this.nothing();
      this.route(focus);
      // Reserves kept in this browser from before the number came first (28 Oct): ask once to save them.
      if (this.asksNumber && this.me.requestsOpen !== false && this.me.mine.reserves.length && !this.askedOld) {
        this.askedOld = true;
        this.askNumber(null, true);
      }
    }

    /** Signed in: everything from the server. Picks still waiting in this browser are saved first. */
    async loadMe(focus = false) {
      if (this.hasPicks()) {
        const picks = this.picks();
        const saved = await this.call('/save', picks);
        if (saved.ok) {
          kept(() => localStorage.removeItem(PICKS));
          this.skipped = (saved.skipped || []).length;
          // "return": signed in from before, with picks still waiting in this browser.
          track('Room Picks Saved', { step: this.saveStep || 'return', reserved: Math.max(0, picks.reserves.length - this.skipped), liked: picks.likes.length, skipped: this.skipped });
          this.saveStep = null;
        } else if (saved.reason !== 'signed_out' && !['closed', 'paused', 'not_open'].includes(saved.reason)) {
          // Kept in this browser for the next try; he is told it did not go through.
          this.flash(saved.reason);
        }
      }
      if (!this.token) return this.room?.state === 'closed' ? this.closed() : this.loadPieces(focus);
      const me = await this.call('/me');
      if (!me.ok) {
        if (me.reason === 'signed_out') return this.room?.state === 'closed' ? this.closed() : this.loadPieces(focus);
        return this.trouble(me.reason, () => this.loadMe(focus));
      }
      this.local = false;
      this.me = me;
      if (me.state === 'closed') return this.results(focus);
      if (me.state === 'not_open') return this.notOpen(this.room?.webOpensAt);
      if (me.state === 'off') return this.nothing();
      this.route(focus);
    }

    /** The first screen, the pieces, a piece or My list, from the address. */
    route(focus = true) {
      if (!this.me) return;
      const hash = location.hash;
      const piece = hash.startsWith('#piece=') && this.me.pieces.find((p) => p.id === decodeURIComponent(hash.slice(7)));
      if (piece) return this.piece(piece, focus);
      if (hash === '#done') return this.done(focus);
      // The first screen, until he has gone in once or picked something.
      if (hash !== '#pieces' && this.local && !this.hasPicks(this.me.mine)) return this.gate(focus);
      this.grid(focus);
    }

    go(hash) {
      if (location.hash !== hash) {
        history.pushState(null, '', hash || location.pathname + location.search);
        this.depth += 1;
      }
      window.scrollTo({ top: 0 });
      this.route(true);
    }

    show(html, { dark = false, focus = true } = {}) {
      this.screen = null;
      // Leaving the pieces: what was on screen is counted now, and where he was in the feed is kept for coming back.
      this.unwatch();
      const feed = this.view.querySelector('[data-room-feed]');
      if (feed) this.feedTop = feed.scrollTop;
      if (this.view.querySelector('.room__grid')) this.gridTop = window.scrollY;
      if (this.sheet.open) this.sheet.close();
      this.classList.toggle('room--dark', dark);
      this.view.innerHTML = html;
      if (focus) this.view.querySelector('[data-room-heading]')?.focus({ preventScroll: true });
    }

    // ─── Screens ─────────────────────────────────────────────────────────────

    // The eyebrow with the small "For KANZE Club members" line under it (the first screen and the pieces).
    kicker(key, tag = 'p') {
      const heading = tag === 'h1' ? ' tabindex="-1" data-room-heading' : '';
      return `<div class="room__kicker"><${tag} class="room__eyebrow"${heading}>${esc(this.t(key))}</${tag}><p class="room__members">${esc(this.t('members'))}</p></div>`;
    }

    rules(prefix) {
      return `<ol class="room__rules">${[1, 2, 3].map((n) => `<li><span class="room__rule-number">${this.num(n)}</span><span>${esc(this.t(`${prefix}.rule_${n}`))}</span></li>`).join('')}</ol>`;
    }

    /** The campaign photo from the dashboard (GET /room imageUrl): on top on a phone, beside the steps on a computer. */
    campaign() {
      const url = this.room?.imageUrl;
      if (!url) return '';
      return `<div class="room__campaign"><img src="${esc(this.sized(url, 900))}" srcset="${esc(this.srcset(url))}" sizes="(min-width: 990px) 50vw, 100vw" alt="" width="1200" height="1500" loading="lazy" data-room-campaign></div>`;
    }

    gate(focus = false) {
      const until = this.me?.closesAt || this.room?.closesAt;
      const image = this.campaign();
      this.show(`
        <div class="room__screen room__screen--gate page-width${image ? ' has-image' : ''}">
          ${image}
          <div class="room__gate-text">
            ${this.kicker('eyebrow')}
            <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(this.t('gate.heading'))}</h1>
            ${this.rules('gate')}
            ${until ? `<p class="room__fine">${esc(this.t('gate.open_until', { date: this.date(until) }))}</p>` : ''}
            <a class="button button--full room__button" href="#pieces" data-room-action="explore">${esc(this.t('gate.explore'))}</a>
          </div>
        </div>`, { dark: true, focus });
    }

    codeScreen() {
      this.show(`
        <div class="room__screen page-width">
          <p class="room__eyebrow">${esc(this.t('code.eyebrow'))}</p>
          <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(this.t('code.heading', { phone: '%phone%' })).replace('%phone%', `<span class="room__number" dir="ltr">${esc(this.masked(this.pending.phone))}</span>`)}</h1>
          <form class="room__form" novalidate data-room-form="verify">
            <label class="visually-hidden" for="RoomOtp">${esc(this.t('code.label'))}</label>
            <input class="room__input room__otp" id="RoomOtp" name="otp" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]*" dir="ltr" required aria-describedby="RoomError">
            <p class="room__error" id="RoomError" role="alert" data-room-error></p>
            <button class="button button--full room__button" type="submit">${esc(this.t('code.continue'))}</button>
          </form>
          <p class="room__fine" data-room-resend></p>
          <button class="link room__link" type="button" data-room-action="change-number">${esc(this.t('code.change'))}</button>
        </div>`, { dark: true });
      this.countdown(this.pending.resendAfter || 60);
    }

    countdown(seconds) {
      window.clearInterval(this.timer);
      const place = this.querySelector('[data-room-resend]');
      const until = Date.now() + seconds * 1000;
      const draw = () => {
        if (!place.isConnected) return window.clearInterval(this.timer);
        const left = Math.max(0, Math.ceil((until - Date.now()) / 1000));
        // (The time is kept left to right inside an Arabic line, as a clock is read: ١:٠٠, not ٠٠:١.)
        if (left) place.textContent = this.t('code.resend_in', { time: '⁦' + this.num(`${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`) + '⁩' });
        else {
          window.clearInterval(this.timer);
          place.innerHTML = `<button class="link room__link" type="button" data-room-action="resend">${esc(this.t('code.resend'))}</button>`;
        }
      };
      draw();
      this.timer = window.setInterval(draw, 1000);
    }

    counts() {
      const mine = this.me.mine || {};
      return { reserved: (mine.reserves || []).length, liked: (mine.likes || []).length };
    }

    /** True while he has picks that are only in this browser. */
    /** The "Ask the WhatsApp number at the first reserve" setting. Off while WhatsApp codes aren't arriving (28 Oct). */
    get asksNumber() {
      return this.dataset.askNumber !== 'false';
    }

    get unsaved() {
      return this.asksNumber && this.local && (this.me.mine?.reserves || []).length > 0;
    }

    /** The pieces: one by one (like Reels) or the grid, with the filters, the switch between them and the bar. */
    grid(focus) {
      // What was on screen before this drawing is counted with the positions it had.
      this.unwatch();
      const pieces = this.me.pieces || [];
      if (!this.entered) { this.entered = true; track('Room Entered'); }
      if (!pieces.length) {
        this.shown = [];
        return this.show(`<div class="room__screen page-width"><p class="room__eyebrow">${esc(this.t('eyebrow'))}</p><h1 class="room__heading" tabindex="-1" data-room-heading>${esc(this.t('room.empty'))}</h1></div>`, { focus });
      }
      const types = [...new Set(pieces.map((p) => p.type).filter(Boolean))];
      if (this.filter !== 'all' && !types.includes(this.filter)) this.filter = 'all';
      const shown = pieces.filter((p) => this.filter === 'all' || p.type === this.filter);
      this.shown = shown.map((p) => p.id);
      const reel = this.mode === 'reel';
      const chips = types.length > 1 ? `<div class="room__chips room__chips--scroll" role="group" aria-label="${esc(this.t('room.all'))}">
          ${['all', ...types].map((type) => `<button class="room__chip" type="button" aria-pressed="${this.filter === type}" data-room-action="filter" data-type="${esc(type)}">${esc(type === 'all' ? this.t('room.all') : this.typeName(type))}</button>`).join('')}
        </div>` : '';
      // The way to look: one icon button beside the title (Ibrahim, 8:40 pm: the filters keep the row to themselves).
      // It shows, and is named by, the view it switches to.
      const next = reel ? 'cards' : 'reel';
      const hint = this.hint ? `<span class="room__hint" aria-hidden="true">${this.chevron()}</span>` : '';
      this.hint = false;
      const head = `<div class="room__head">${this.kicker('eyebrow', 'h1')}<span class="room__view-wrap">${hint}<button class="room__view-button" type="button" aria-label="${esc(this.t(next === 'reel' ? 'room.view_reel' : 'room.view_cards'))}" data-room-action="view" data-mode="${next}">${this.viewIcon(next)}</button></span></div>`;
      const paused = this.requestsOpen ? '' : `<p class="room__paused">${esc(this.t('piece.paused'))}</p>`;
      const bar = `<div class="room__bar">
            <p class="room__bar-text" data-room-bar>${this.barText()}</p>
            <a class="button button--secondary room__bar-button" href="#done" data-room-action="done">${esc(this.t('room.my_list'))}</a>
          </div>`;
      if (reel) {
        this.show(`
          <div class="room__screen room__screen--reel page-width">
            ${head}
            ${chips}
            ${paused}
            <ul class="room__reel" role="list" aria-label="${esc(this.t('room.feed_label'))}" tabindex="0" data-room-feed>
              ${shown.map((p, at) => this.reelItem(p, at)).join('')}
            </ul>
            ${bar}
          </div>`, { focus });
        const feed = this.view.querySelector('[data-room-feed]');
        if (this.feedTop) feed.scrollTop = this.feedTop;
        // The feed fills the screen under the header, as Reels do.
        const screen = this.view.querySelector('.room__screen--reel');
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-height')) || 0;
        const top = Math.max(0, screen.getBoundingClientRect().top + window.scrollY - header);
        if (Math.abs(window.scrollY - top) > 2) window.scrollTo({ top });
        this.watch(feed.querySelectorAll('[data-piece]'), feed, 'reel');
        feed.querySelectorAll('[data-piece]').forEach((item) => this.ahead?.observe(item));
        this.resizer.disconnect();
        this.resizer.observe(feed);
        this.fit();
        return;
      }
      const likes = this.me.mine?.likes || [];
      const reserves = this.me.mine?.reserves || [];
      this.show(`
        <div class="room__screen room__screen--wide page-width">
          <div class="room__top">
            ${head}
            <p class="room__intro"><b>${esc(this.t('room.intro'))}</b> ${esc(this.t('room.intro_more'))}</p>
          </div>
          ${paused}
          ${chips}
          <ul class="room__grid" role="list">
            ${shown.map((p) => this.card(p)).join('')}
          </ul>
          ${bar}
        </div>`, { focus });
      this.watch(this.view.querySelectorAll('.room__card[data-piece]'), null, 'cards');
      this.view.querySelectorAll('.room__card[data-piece]').forEach((card) => this.ahead?.observe(card));
      // Back from a piece: the grid where he left it.
      if (this.gridTop && !focus) window.scrollTo({ top: this.gridTop });
    }

    /** One card of the grid, or of the home row (rail): the photos of the colour on screen, the circles, the name, the fit, the price, the heart. */
    card(p) {
      const likes = this.me.mine?.likes || [];
      const reserves = this.me.mine?.reserves || [];
      const isLiked = likes.includes(p.id);
      const reserve = reserves.find((r) => r.pieceId === p.id);
      const images = this.photosFor(p);
      const fit = this.fitLine(p);
      // The card's own photos (of the colour on screen) slide sideways, with dots; arrows with a mouse (11:54 pm).
      const href = `${this.rail ? this.dataset.pageUrl || '' : ''}#piece=${encodeURIComponent(p.id)}`;
      return `<li class="room__card${this.rail ? ' rail__item' : ''}" data-piece="${esc(p.id)}">
        <div class="room__card-media"${this.shadeStyle(p)}>
          <ul class="room__card-photos" role="list" data-room-photos data-id="${esc(p.id)}">${this.cardPhotos(p, images)}</ul>
          ${p.inStore ? `<span class="room__badge">${esc(this.t('piece.in_store'))}</span>` : ''}
          ${images.length > 1 ? this.photoSteps(esc(p.id), images) : ''}
        </div>
        <a class="room__card-link" href="${esc(href)}" data-room-action="open" data-id="${esc(p.id)}">
          <span class="room__card-title">${esc(p.title)}</span>
          ${fit ? `<span class="room__card-meta" dir="auto">${esc(fit)}</span>` : ''}
          <span class="room__card-price">${esc(this.price(p))}</span>
          <span class="room__card-reserved" data-room-reserved="${esc(p.id)}"${reserve ? '' : ' hidden'}>${reserve ? esc(this.t('piece.reserved', { size: this.sizeText(reserve) })) : ''}</span>
        </a>
        ${this.shades(p, 'shade', true)}
        ${this.requestsOpen && !p.inStore ? `<button class="room__like" type="button" aria-pressed="${isLiked}" aria-label="${esc(this.t('room.like_label', { title: p.title }))}" data-room-action="like" data-id="${esc(p.id)}">${this.heart()}</button>` : ''}
      </li>`;
    }

    /** "Reserved 1 · Liked 2" and the line under it (not saved yet, or open until). */
    barText() {
      const { reserved, liked } = this.counts();
      const note = this.unsaved ? `<span class="room__unsaved">${esc(this.t('save.not_saved'))}</span>` : this.me.closesAt ? `<span>${esc(this.t('room.bar_note', { date: this.date(this.me.closesAt) }))}</span>` : '';
      return `<b>${esc(this.t('room.bar', { reserved: this.num(reserved), liked: this.num(liked) }))}</b>${note}`;
    }

    sizeText(reserve) {
      return [reserve.color && this.colorName(reserve.color), reserve.size].filter(Boolean).join(' / ');
    }

    /** One piece of the feed: its photos whole and swiped sideways, then the name, the price and ♥ / Reserve my size / ✕. */
    reelItem(p, at) {
      const images = this.photosFor(p);
      const id = esc(p.id);
      const reserve = (this.me.mine?.reserves || []).find((r) => r.pieceId === p.id);
      const liked = (this.me.mine?.likes || []).includes(p.id);
      const reason = Boolean(this.me.mine?.reasons?.[p.id]);
      const many = images.length > 1;
      let actions = '';
      if (p.inStore) {
        actions = `<a class="button room__reel-shop" href="${esc(p.inStore.url)}">${esc(this.t('piece.shop_now'))}</a>`;
      } else if (this.requestsOpen) {
        actions = `<div class="room__reel-actions">
            <button class="room__square" type="button" aria-pressed="${liked}" aria-label="${esc(this.t('room.like_label', { title: p.title }))}" data-room-action="like" data-id="${id}">${this.heart()}</button>
            <button class="button room__reel-reserve${reserve ? ' is-reserved' : ''}" type="button" aria-haspopup="dialog" data-room-action="sheet" data-id="${id}">${esc(reserve ? this.t('piece.change_reserve') : this.t('piece.reserve'))}</button>
            <button class="room__square" type="button" aria-pressed="${reason}" aria-haspopup="dialog" aria-label="${esc(this.t('piece.not_for_me_short'))}" data-room-action="reasons" data-id="${id}">${this.cross()}</button>
          </div>`;
      }
      return `<li class="room__reel-item" data-piece="${id}">
          <div class="room__reel-media"${this.shadeStyle(p)}>
            <ul class="room__reel-photos" role="list" data-room-photos data-id="${id}">
              ${this.reelPhotos(p, images, at)}
            </ul>
            ${p.inStore ? `<span class="room__badge">${esc(this.t('piece.in_store'))}</span>` : ''}
            <span class="room__badge room__badge--reserved" data-room-reserved="${id}"${reserve ? '' : ' hidden'}>${esc(this.t('piece.reserved_badge'))}</span>
            ${many ? this.photoSteps(id, images) : ''}
          </div>
          <div class="room__reel-strip">
            <a class="room__reel-title" href="#piece=${encodeURIComponent(p.id)}" data-room-action="open" data-id="${id}">
              <span class="room__reel-name">${esc(p.title)}</span>
              <span class="room__reel-price">${esc(this.price(p))}</span>
              ${this.fitLine(p) ? `<span class="room__reel-meta" dir="auto">${esc(this.fitLine(p))}</span>` : ''}
            </a>
            ${this.shades(p)}
            ${actions}
          </div>
        </li>`;
    }

    reelPhotos(p, images, at = 9) {
      const id = esc(p.id);
      return images.map((image, n) => `<li class="room__reel-photo" data-room-action="reel-photo" data-id="${id}" data-src="${esc(this.sized(image.url, 2000))}" data-srcset="${esc([900, 1400, 2000, 2800].map((w) => `${this.sized(image.url, w)} ${w}w`).join(', '))}" data-width="900" data-height="1125"><img src="${esc(this.sized(image.url, 900))}" srcset="${esc(this.srcset(image.url))}" sizes="(min-width: 750px) 560px, 100vw" alt="${esc(image.alt || this.t('piece.photo', { number: this.num(n + 1), total: this.num(images.length) }))}" width="900" height="1125"${at > 1 || n ? ' loading="lazy"' : ''} decoding="async"></li>`).join('');
    }

    cardPhotos(p, images) {
      const id = esc(p.id);
      return images.map((image, n) => `<li class="room__card-photo" data-room-action="open" data-id="${id}"><img src="${esc(this.sized(image.url, 600))}" srcset="${esc(this.srcset(image.url))}" sizes="(min-width: 750px) 25vw, 50vw" alt="${esc(image.alt || '')}" width="600" height="750" loading="lazy" decoding="async"></li>`).join('');
    }

    /**
     * The expected price as the page's language writes it. The shop types it in English
     * ("about 23.900 KD", "about 22–25 KD"); on Arabic pages it becomes «حوالي ٢٣٫٩٠٠ د.ك» with
     * Arabic digits (the words are the language files'). Anything else shows as typed.
     */
    price(p) {
      const text = p.priceText || '';
      if (!this.ar || !text) return text;
      const found = text.match(/([\d.,]+)(?:\s*[–-]\s*([\d.,]+))?\s*(?:KD|KWD)/i);
      if (!found) return text;
      const arabic = (value) => this.num(value.replace(/,/g, '')).replace(/\./g, '٫');
      const amount = found[2] ? `${arabic(found[1])}–${arabic(found[2])}` : arabic(found[1]);
      return this.t(/about|approx|~/i.test(text) ? 'piece.price_about' : 'piece.price_exact', { price: amount });
    }

    /** The fit under the name on the cards and in the feed (11:53 pm): the piece's fit note, in Arabic on Arabic pages when the server has it. */
    fitLine(p) {
      return (this.ar && p.fitNoteAr) || p.fitNote || '';
    }

    /** The fabric on the piece page: in Arabic on Arabic pages when the server has it (board KZ-20261029-w27-arabic-fabric-web). */
    fabricLine(p) {
      return (this.ar && p.fabricAr) || p.fabric || '';
    }

    photoSteps(id, images) {
      return `
              <button class="room__photo-step room__photo-step--prev" type="button" aria-label="${esc(this.t('piece.photo_prev'))}" data-room-action="photo" data-id="${id}" data-step="-1" disabled>${this.chevron()}</button>
              <button class="room__photo-step room__photo-step--next" type="button" aria-label="${esc(this.t('piece.photo_next'))}" data-room-action="photo" data-id="${id}" data-step="1">${this.chevron()}</button>
              <span class="room__dots" aria-hidden="true">${images.map((image, n) => `<span class="room__dot${n ? '' : ' is-on'}"></span>`).join('')}</span>`;
    }

    // ─── Colours (Ibrahim, 24 Oct 11:31 pm) ──────────────────────────────────

    remember(id, color) {
      this.colors[id] = color;
      kept(() => sessionStorage.setItem(COLORS, JSON.stringify(this.colors)));
    }

    /**
     * Every colour's first photo of a piece near the screen, fetched ahead at the size it will
     * show (the same srcset and sizes, so the browser takes it from its cache when the colour is
     * picked): a new colour then shows at once instead of after the network.
     */
    preload(element, piece = null, sizesFor = null) {
      const p = piece || this.me?.pieces.find((one) => one.id === element.dataset.piece);
      if (!p || (p.colors || []).length < 2) return;
      const sizes = sizesFor || (element.classList.contains('room__card') ? '(min-width: 750px) 25vw, 50vw' : '(min-width: 750px) 560px, 100vw');
      for (const color of p.colors) {
        const photo = (p.images || []).find((image) => this.imageColor(p, image) === color);
        if (!photo) continue;
        const ahead = new Image();
        ahead.decoding = 'async';
        ahead.sizes = sizes;
        ahead.srcset = this.srcset(photo.url);
        ahead.src = this.sized(photo.url, 600);
      }
    }

    /** The shade a photo box shows while its photo is on the way: the colour's own (no blank box). */
    shadeStyle(p) {
      const color = this.colorOf(p);
      return color ? ` style="--photo-shade: ${esc(p.colorSwatches?.[color] || this.hex(p, color))}"` : '';
    }

    /** The colour on screen for a piece: the one he picked, else his reserve's or like's, else the first. */
    colorOf(p) {
      return this.choiceFor(p, (this.me.mine?.reserves || []).find((r) => r.pieceId === p.id)).color;
    }

    /** The photos of the colour on screen, and only those. */
    photosFor(p) {
      const images = p.images || [];
      const color = this.colorOf(p);
      const of = (image) => this.imageColor(p, image);
      if (!color || !images.some(of)) return images;
      // Only that colour's photos, in the shop's order (Ibrahim, 11:52 pm). A colour with no photo
      // of its own shows the photos with no colour, else all of them.
      const own = images.filter((image) => of(image) === color);
      if (own.length) return own;
      const shared = images.filter((image) => !of(image));
      return shared.length ? shared : images;
    }

    /** A photo's colour: the server's, else the end of its description ("The Pleat Trousers - Navy"), the same rule as the app. */
    imageColor(p, image) {
      if (image.color) return image.color;
      const end = String(image.alt || '').split(' - ').pop().trim().toLowerCase();
      return (p.colors || []).find((color) => color.toLowerCase() === end) || null;
    }

    /** A colour's name in the page's language (the list in the language files), else as the shop writes it. */
    /** "Black=أسود, Brown=بني" (a line in the language file) as a lookup by the English name. */
    names(key) {
      return Object.fromEntries((this.t(key) || '').split(/[,،\n]/).map((pair) => pair.split('=').map((part) => part.trim())).filter((pair) => pair.length === 2 && pair[0] && pair[1]).map(([en, local]) => [en.toLowerCase(), local]));
    }

    colorName(color) {
      this.colorNames ||= this.names('color_names');
      return this.colorNames[String(color).toLowerCase()] || color;
    }

    /** The server sends the kind of piece in English ("Trousers"): on /ar it is worded from the language file (type_names). */
    typeName(type) {
      this.typeNames ||= this.names('type_names');
      return this.typeNames[String(type).toLowerCase()] || type;
    }

    hex(p, color) {
      return p.colorSwatches?.[color] || SHADES[String(color).toLowerCase()] || this.swatchModule?.fallbackHexForName?.(color) || '#8a8a8a';
    }

    /**
     * The colour circles of a piece, as on the product pages (Ibrahim, 11:52 pm): each painted from
     * that colour's own photo by <swatch-dot> (assets/swatch-dot.js, the app's rules), or from the
     * shop's swatch when the server sends one. The one on screen is ringed. On the cards and in the
     * feed a circle switches the photos; on the piece page and in the sheet it is the colour picker.
     */
    shades(p, mode = 'shade', cards = false) {
      const colors = p.colors || [];
      if (colors.length < (mode === 'choose' ? 1 : 2)) return '';
      const on = this.colorOf(p);
      const images = p.images || [];
      const circle = (color) => {
        const photo = images.find((image) => this.imageColor(p, image) === color);
        const own = p.colorSwatches?.[color] || SHADES[String(color).toLowerCase()];
        const reader = !own && photo ? `<swatch-dot data-name="${esc(color)}" data-image="${esc(this.sized(photo.url, 96))}" data-text="${esc([p.type, p.title].filter(Boolean).join(' '))}"></swatch-dot>` : '';
        return `<button class="room__shade" type="button" role="radio" aria-checked="${color === on}" aria-label="${esc(this.colorName(color))}" title="${esc(this.colorName(color))}" data-room-action="${mode}" data-id="${esc(p.id)}"${mode === 'choose' ? ' data-kind="color"' : ''} data-value="${esc(color)}"><span class="room__shade-dot" style="--dot-color: ${esc(own || this.hex(p, color))}">${reader}</span></button>`;
      };
      const name = mode === 'choose' ? `<p class="room__shade-name">${esc(this.t('piece.colour'))}: <b>${esc(this.colorName(on))}</b></p>` : '';
      // On a card: four circles, or three (the chosen one among them) and "+N" that opens the piece, as in the app.
      let shown = colors;
      let more = '';
      if (cards && colors.length > 4) {
        shown = colors.slice(0, 3);
        if (!shown.includes(on)) shown = [...colors.slice(0, 2), on];
        more = `<a class="room__shade-more" href="#piece=${encodeURIComponent(p.id)}" data-room-action="open" data-id="${esc(p.id)}" aria-label="${esc(this.t('piece.more_colours', { count: this.num(colors.length - 3) }))}">+${esc(this.num(colors.length - 3))}</a>`;
      }
      return `${name}<div class="room__shades" role="radiogroup" aria-label="${esc(this.t('piece.colour'))}"${mode === 'choose' ? ' data-room-options="color"' : ''}>${shown.map(circle).join('')}${more}</div>`;
    }

    /** Another colour picked (a dot, or the sheet): that piece's photos and dots change where they are. */
    recolor(p) {
      const id = p.id;
      const on = this.colorOf(p);
      this.querySelectorAll(`[data-room-action="shade"][data-id="${CSS.escape(id)}"]`).forEach((dot) => dot.setAttribute('aria-checked', String(dot.dataset.value === on)));
      const cardShades = this.view.querySelector(`.room__card[data-piece="${CSS.escape(id)}"] .room__shades`);
      if (cardShades && !cardShades.querySelector(`[data-value="${CSS.escape(on)}"]`)) cardShades.outerHTML = this.shades(p, 'shade', true);
      const images = this.photosFor(p);
      const shade = p.colorSwatches?.[on] || this.hex(p, on);
      this.view.querySelectorAll(`[data-piece="${CSS.escape(id)}"] .room__card-media, [data-piece="${CSS.escape(id)}"] .room__reel-media`).forEach((media) => media.style.setProperty('--photo-shade', shade));
      const item = this.view.querySelector(`.room__reel-item[data-piece="${CSS.escape(id)}"]`);
      if (item) {
        const media = item.querySelector('.room__reel-media');
        const strip = media.querySelector('[data-room-photos]');
        media.classList.remove('is-loaded');
        strip.innerHTML = this.reelPhotos(p, images, 0);
        strip.scrollLeft = 0;
        strip.dataset.index = 0;
        media.querySelectorAll('.room__photo-step, .room__dots').forEach((part) => part.remove());
        if (images.length > 1) media.insertAdjacentHTML('beforeend', this.photoSteps(esc(id), images));
      }
      const card = this.view.querySelector(`.room__card[data-piece="${CSS.escape(id)}"] .room__card-media`);
      if (card) {
        const strip = card.querySelector('[data-room-photos]');
        card.classList.remove('is-loaded');
        strip.innerHTML = this.cardPhotos(p, images);
        strip.scrollLeft = 0;
        strip.dataset.index = 0;
        card.querySelectorAll('.room__photo-step, .room__dots').forEach((part) => part.remove());
        if (images.length > 1) card.insertAdjacentHTML('beforeend', this.photoSteps(esc(id), images));
      }
    }

    /**
     * One photo box for every piece and every photo of the feed, so nothing changes size while
     * swiping, sideways or down (Ibrahim, 29 Oct): the room's photo shape (Section > Photo shape),
     * as tall as the piece with the most lines under its photo allows, centred. The photos fill it
     * (CSS: object-fit cover). Never wider than the screen.
     */
    ratio() {
      const [w, h] = getComputedStyle(this).getPropertyValue('--room-photo-ratio').split('/').map(Number);
      return w > 0 && h > 0 ? w / h : 2 / 3;
    }

    fit() {
      const feed = this.view?.querySelector('[data-room-feed]');
      if (!feed) return;
      const items = [...feed.querySelectorAll('.room__reel-item')];
      feed.classList.remove('is-fitted');
      const height = Math.min(...items.map((item) => item.querySelector('.room__reel-media')?.clientHeight || Infinity));
      if (!Number.isFinite(height) || !height) return;
      const width = Math.round(height * this.ratio());
      feed.style.setProperty('--photo-height', `${height}px`);
      feed.classList.add('is-fitted');
      items.forEach((item) => {
        const fits = Math.min(item.clientWidth, width);
        item.style.setProperty('--card-width', `${fits}px`);
        item.classList.toggle('is-narrow', fits < item.clientWidth - 1);
        const media = item.querySelector('.room__reel-media');
        if (media?.querySelector('.room__reel-photo img')?.naturalWidth) media.classList.add('is-loaded');
      });
    }

    /** After a like, a reserve or a reason on the pieces: the buttons and the bar change in place, so the feed stays where it is. */
    paint() {
      const mine = this.me.mine || empty();
      const view = this.view;
      view.querySelectorAll('[data-room-action="like"][data-id]').forEach((button) => button.setAttribute('aria-pressed', String(mine.likes.includes(button.dataset.id))));
      view.querySelectorAll('[data-room-action="reasons"][data-id]').forEach((button) => button.setAttribute('aria-pressed', String(Boolean(mine.reasons[button.dataset.id]))));
      view.querySelectorAll('[data-room-action="sheet"][data-id]').forEach((button) => {
        const reserved = mine.reserves.some((r) => r.pieceId === button.dataset.id);
        button.textContent = reserved ? this.t('piece.change_reserve') : this.t('piece.reserve');
        button.classList.toggle('is-reserved', reserved);
      });
      view.querySelectorAll('[data-room-reserved]').forEach((badge) => {
        const reserve = mine.reserves.find((r) => r.pieceId === badge.dataset.roomReserved);
        badge.hidden = !reserve;
        if (badge.classList.contains('room__card-reserved')) badge.textContent = reserve ? this.t('piece.reserved', { size: this.sizeText(reserve) }) : '';
      });
      const bar = view.querySelector('[data-room-bar]');
      if (bar) bar.innerHTML = this.barText();
    }

    /** How he is looking at a piece now, for its events: the piece page, or the feed or grid with its place in the list. */
    where(id) {
      if (location.hash.startsWith('#piece=')) return { view_mode: 'detail' };
      const at = this.shown.indexOf(id);
      return { view_mode: this.mode, ...(at >= 0 ? { position: at + 1 } : {}) };
    }

    // ─── Interest: what was seen, and for how long ───────────────────────────

    /** Counts the pieces as they come on screen and go (the app's createImpressionTracker, with the browser's own observer). */
    watch(items, root, mode) {
      this.unwatch();
      if (!('IntersectionObserver' in window) || !items.length) return;
      this.seenSince = new Map();
      this.seenMode = mode;
      this.seenShown = this.shown;
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const id = entry.target.dataset.piece;
          if (entry.isIntersecting && entry.intersectionRatio >= SEEN[mode] - 0.01) {
            if (!this.seenSince.has(id) && !document.hidden) this.seenSince.set(id, performance.now());
          } else this.impression(id);
        }
      }, { root, threshold: [SEEN[mode]] });
      items.forEach((item) => this.observer.observe(item));
    }

    unwatch() {
      if (!this.seenSince) return;
      this.observer?.disconnect();
      [...this.seenSince.keys()].forEach((id) => this.impression(id));
      this.observer = null;
      this.seenSince = null;
    }

    impression(id) {
      const since = this.seenSince?.get(id);
      if (since === undefined) return;
      this.seenSince.delete(id);
      const dwell = Math.round(performance.now() - since);
      if (dwell < DWELL) return;
      const piece = this.me?.pieces.find((p) => p.id === id);
      track('Piece Impression', { piece_id: id, piece_title: piece?.title, position: this.seenShown.indexOf(id) + 1, view_mode: this.seenMode, dwell_ms: dwell });
    }

    onVisibility() {
      if (!this.seenSince) return;
      if (document.hidden) {
        this.away = [...this.seenSince.keys()];
        this.away.forEach((id) => this.impression(id));
      } else {
        const now = performance.now();
        (this.away || []).forEach((id) => this.seenSince.set(id, now));
        this.away = [];
      }
    }

    /** A photo strip came to rest: when it is on another photo, the dots and arrows follow and the swipe is counted. */
    onScroll(event) {
      const strip = event.target.closest?.('[data-room-photos]');
      if (!strip) return;
      window.clearTimeout(strip.restTimer);
      strip.restTimer = window.setTimeout(() => this.swiped(strip), 120);
    }

    swiped(strip) {
      const step = strip.firstElementChild?.getBoundingClientRect().width;
      if (!step) return;
      const count = strip.children.length;
      const index = Math.min(count - 1, Math.round(Math.abs(strip.scrollLeft) / step));
      if (index === Number(strip.dataset.index || 0)) return;
      strip.dataset.index = index;
      const media = strip.parentElement;
      media.querySelectorAll('.room__dot').forEach((dot, n) => dot.classList.toggle('is-on', n === index));
      const prev = media.querySelector('[data-step="-1"]');
      const next = media.querySelector('[data-step="1"]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index >= count - Math.round(strip.clientWidth / step);
      const piece = this.me?.pieces.find((p) => p.id === strip.dataset.id);
      if (piece) track('Piece Image Swiped', { piece_id: piece.id, piece_title: piece.title, image_index: index, image_count: count, ...this.where(piece.id) });
    }

    // ─── The sheets: Reserve your size, Not for me ───────────────────────────

    openSheet(kind, piece) {
      this.sheetFor = { kind, id: piece.id };
      this.drawSheet();
      if (!this.sheet.open) this.sheet.showModal();
      this.sheet.querySelector('[data-room-sheet-first]')?.focus();
      if (kind === 'reserve') track('Reserve Sheet Opened', { piece_id: piece.id, piece_title: piece.title, ...this.where(piece.id) });
    }

    drawSheet(focusKey) {
      if (['phone', 'code', 'confirm'].includes(this.sheetFor?.kind)) return this.drawNumberSheet();
      const p = this.me.pieces.find((piece) => piece.id === this.sheetFor?.id);
      if (!p) return this.sheet.open && this.sheet.close();
      const head = (title) => `<div class="room__sheet-head">
          <div>
            <p class="room__sheet-title" id="RoomSheetTitle">${esc(title)}</p>
            <p class="room__meta">${esc([p.title, this.price(p)].filter(Boolean).join(' · '))}</p>
          </div>
          <button class="room__close" type="button" aria-label="${esc(this.t('piece.close'))}" data-room-action="close-sheet">${this.cross()}</button>
        </div>`;
      if (this.sheetFor.kind === 'reasons') {
        const reason = this.me.mine?.reasons?.[p.id] || null;
        this.sheet.innerHTML = `<div class="room__sheet-body">
            ${head(this.t('piece.not_for_me'))}
            <div class="room__chips room__chips--wide" role="group" aria-label="${esc(this.t('piece.not_for_me'))}">
              ${REASONS.map((r, n) => `<button class="room__chip" type="button" aria-pressed="${reason === r}" data-room-action="reason" data-id="${esc(p.id)}" data-reason="${r}"${n ? '' : ' data-room-sheet-first'}>${esc(this.t(`piece.reason_${r}`))}</button>`).join('')}
            </div>
            <p class="room__error" role="alert" data-room-error></p>
          </div>`;
        return;
      }
      const reserve = (this.me.mine?.reserves || []).find((r) => r.pieceId === p.id);
      const choice = this.choiceFor(p, reserve);
      this.sheet.innerHTML = `<div class="room__sheet-body">
          ${head(this.t('piece.sheet_title'))}
          ${this.optionRows(p, choice)}
          ${this.reserveButton(p, reserve, choice)}
          <p class="room__error" role="alert" data-room-error></p>
          ${reserve ? `<button class="link room__link" type="button" data-room-action="unreserve" data-id="${esc(p.id)}">${esc(this.t('piece.cancel'))}</button>` : ''}
          <p class="room__fine">${esc(this.t('piece.what_text'))}</p>
        </div>`;
      const first = this.sheet.querySelector('[data-room-options] [aria-checked="true"], [data-room-options] button, .room__reserve');
      first?.setAttribute('data-room-sheet-first', '');
      if (focusKey) this.sheet.querySelector(focusKey)?.focus();
    }

    // ─── A piece ─────────────────────────────────────────────────────────────

    /** The colour and size picked for a piece (his reserve's, or the only one there is). */
    choiceFor(p, reserve) {
      const colors = p.colors || [];
      const sizes = p.sizes || [];
      // The colour on screen is the one picked for a reserve too: his reserve's, his like's, else the first.
      return this.choice[p.id] || (this.choice[p.id] = {
        color: (colors.includes(this.colors?.[p.id]) && this.colors[p.id]) || reserve?.color || this.me?.mine?.likeColors?.[p.id] || colors[0] || null,
        size: reserve?.size || (sizes.length === 1 ? sizes[0] : null),
      });
    }

    optionRows(p, choice) {
      const options = (name, list, chosen, kind) => (list.length > 1 ? `
        <div class="room__options" role="radiogroup" aria-label="${esc(name)}" data-room-options="${kind}">
          ${list.map((value) => `<button class="room__option room__option--${kind}" type="button" role="radio" aria-checked="${chosen === value}" data-room-action="choose" data-id="${esc(p.id)}" data-kind="${kind}" data-value="${esc(value)}">${esc(kind === 'color' ? this.colorName(value) : value)}</button>`).join('')}
        </div>` : '');
      return this.shades(p, 'choose') + options(this.t('piece.size'), p.sizes || [], choice.size, 'size');
    }

    reserveButton(p, reserve, choice, extra = '') {
      const same = reserve && reserve.color === choice.color && reserve.size === choice.size;
      const label = reserve ? (same ? this.t('piece.reserved', { size: this.sizeText(reserve) }) : this.t('piece.change', { size: this.sizeText(choice) }))
        : choice.size ? this.t('piece.reserve_size', { size: choice.size }) : this.t('piece.reserve');
      return `<button class="button room__button room__reserve${extra}" type="button" data-room-action="reserve" data-id="${esc(p.id)}"${same ? ' aria-disabled="true"' : ''}>${esc(label)}</button>`;
    }

    piece(p, focus) {
      track('Piece Viewed', { piece_id: p.id, piece_title: p.title, view_mode: 'detail' });
      this.preload(null, p, '(min-width: 750px) 50vw, 100vw');
      const reserve = (this.me.mine?.reserves || []).find((r) => r.pieceId === p.id);
      const liked = (this.me.mine?.likes || []).includes(p.id);
      const reason = this.me.mine?.reasons?.[p.id] || null;
      const choice = this.choiceFor(p, reserve);
      const images = this.photosFor(p);
      let actions = '';
      if (p.inStore) {
        actions = `<p class="room__in-store">${esc(this.t('piece.in_store'))}</p><a class="button button--full room__button" href="${esc(p.inStore.url)}">${esc(this.t('piece.shop_now'))}</a>`;
      } else if (this.requestsOpen) {
        actions = `
          <div class="room__actions">
            ${this.reserveButton(p, reserve, choice)}
            <button class="button button--secondary room__button room__like-button" type="button" aria-pressed="${liked}" data-room-action="like" data-id="${esc(p.id)}">${this.heart()}<span>${esc(liked ? this.t('room.liked') : this.t('room.like'))}</span></button>
          </div>
          <p class="room__error" role="alert" data-room-error></p>
          ${reserve ? `<button class="link room__link" type="button" data-room-action="unreserve" data-id="${esc(p.id)}">${esc(this.t('piece.cancel'))}</button>` : ''}`;
      } else {
        actions = `<p class="room__paused">${esc(this.t('piece.paused'))}</p>`;
      }
      this.show(`
        <div class="room__screen room__screen--piece page-width">
          <a class="link room__back" href="#pieces" data-room-action="back">${esc(this.t('piece.back'))}</a>
          <div class="room__gallery" data-room-gallery${this.shadeStyle(p)}>
            <ul class="room__photos" role="list" data-room-photos data-id="${esc(p.id)}">
              ${images.map((image, at) => `<li class="room__photo"><button class="room__zoom" type="button" data-room-action="zoom" data-zoom data-src="${esc(this.sized(image.url, 2000))}" data-srcset="${esc([900, 1400, 2000, 2800].map((w) => `${this.sized(image.url, w)} ${w}w`).join(', '))}" data-width="900" data-height="1125" aria-label="${esc(this.t('piece.zoom', { number: this.num(at + 1) }))}"><img src="${esc(this.sized(image.url, 900))}" srcset="${esc(this.srcset(image.url))}" sizes="(min-width: 750px) 50vw, 100vw" alt="${esc(image.alt || this.t('piece.photo', { number: this.num(at + 1), total: this.num(images.length) }))}" width="900" height="1125"${at ? ' loading="lazy"' : ''}></button></li>`).join('')}
            </ul>
          </div>
          <div class="room__details">
            <div class="room__title-row">
              <h1 class="room__title" tabindex="-1" data-room-heading>${esc(p.title)}</h1>
              <p class="room__price">${esc(this.price(p))}</p>
            </div>
            <p class="room__meta">${[this.fabricLine(p), this.fitLine(p)].filter(Boolean).map((part) => `<bdi>${esc(part)}</bdi>`).join('')}</p>
            ${p.inStore ? '' : this.optionRows(p, choice)}
            ${actions}
            <div class="room__what">
              <p class="room__what-title">${esc(this.t('piece.what_title'))}</p>
              <p>${esc(this.t('piece.what_text'))}</p>
            </div>
            ${this.requestsOpen && !p.inStore ? `
              <p class="room__label">${esc(this.t('piece.not_for_me'))}</p>
              <div class="room__chips" role="group" aria-label="${esc(this.t('piece.not_for_me'))}">
                ${REASONS.map((r) => `<button class="room__chip" type="button" aria-pressed="${reason === r}" data-room-action="reason" data-id="${esc(p.id)}" data-reason="${r}">${esc(this.t(`piece.reason_${r}`))}</button>`).join('')}
              </div>` : ''}
          </div>
        </div>`, { focus });
    }

    // ─── His WhatsApp number, at the first reserve (Ibrahim, 28 Oct) ─────────

    /**
     * The first "Reserve my size" without a number: the number sheet, then the 6-digit WhatsApp
     * code, then this reserve and everything picked so far are saved at once, and a confirmation.
     * `old`: reserves kept in this browser from before (the earlier "phone at Done" flow), saved the same way.
     */
    askNumber(pending, old = false) {
      this.pendingReserve = pending;
      this.sheetFor = { kind: 'phone', id: pending?.pieceId || null, old };
      this.drawSheet();
      if (!this.sheet.open) this.sheet.showModal();
      this.sheet.querySelector('#RoomNumber')?.focus();
    }

    drawNumberSheet() {
      const kind = this.sheetFor.kind;
      const old = this.sheetFor.old;
      const head = (title, extra = '') => `<div class="room__sheet-head">
          <div><p class="room__sheet-title" id="RoomSheetTitle">${title}</p>${extra}</div>
          <button class="room__close" type="button" aria-label="${esc(this.t('piece.close'))}" data-room-action="close-sheet">${this.cross()}</button>
        </div>`;
      if (kind === 'phone') {
        this.sheet.innerHTML = `<div class="room__sheet-body">
            ${head(esc(this.t(old ? 'save.title_old' : 'phone.title')))}
            <form class="room__form room__number" novalidate data-room-form="save">
              <label class="visually-hidden" for="RoomNumber">${esc(this.t('phone.title'))}</label>
              <div class="room__phone" dir="ltr">
                <select class="room__input room__code" name="code" aria-label="${esc(this.t('save.phone_label'))}" autocomplete="tel-country-code">
                  ${CODES.map(([c]) => `<option value="${c}"${(this.pending?.code || '965') === c ? ' selected' : ''}>+${c}</option>`).join('')}
                </select>
                <input class="room__input" id="RoomNumber" name="number" type="tel" inputmode="tel" autocomplete="tel-national" required aria-describedby="RoomNumberWhy RoomError" value="${esc(this.pending?.number || '')}">
              </div>
              <p class="room__fine" id="RoomNumberWhy">${esc(this.t('phone.why'))}</p>
              <p class="room__error" id="RoomError" role="alert" data-room-error></p>
              <button class="button button--full room__button" type="submit">${esc(this.t(old ? 'save.button' : 'phone.button'))}</button>
            </form>
          </div>`;
        return;
      }
      if (kind === 'code') {
        const heading = esc(this.t('code.heading', { phone: '%phone%' })).replace('%phone%', `<span class="room__number" dir="ltr">${esc(this.masked(this.pending.phone))}</span>`);
        this.sheet.innerHTML = `<div class="room__sheet-body">
            ${head(heading)}
            <form class="room__form" novalidate data-room-form="verify">
              <label class="visually-hidden" for="RoomOtp">${esc(this.t('code.label'))}</label>
              <input class="room__input room__otp" id="RoomOtp" name="otp" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]*" dir="ltr" required aria-describedby="RoomError">
              <p class="room__error" id="RoomError" role="alert" data-room-error></p>
              <button class="button button--full room__button" type="submit">${esc(this.t('code.continue'))}</button>
            </form>
            <p class="room__fine" data-room-resend></p>
            <button class="link room__link" type="button" data-room-action="change-number">${esc(this.t('code.change'))}</button>
          </div>`;
        return;
      }
      this.sheet.innerHTML = `<div class="room__sheet-body room__confirm" role="status">
          ${head(`<span class="room__tick" aria-hidden="true"></span>`)}
          <p class="room__confirm-line"><b>${esc(this.t('confirm.line_1'))}</b></p>
          <p class="room__text">${esc(this.t('confirm.line_2'))}</p>
          <button class="button button--full room__button" type="button" data-room-action="close-sheet" data-room-sheet-first>${esc(this.t('confirm.button'))}</button>
        </div>`;
    }

    /**
     * Signed in now: the reserve waiting in the sheet, the reserves kept in this browser from
     * before, and his likes and reasons are saved at once; yes to the arrival message on WhatsApp
     * (the sheet's line says so). "Size Reserved" is counted here, after the save.
     */
    async saveNumbered() {
      const picks = this.picks();
      const pending = this.pendingReserve;
      const reserves = picks.reserves.filter((r) => !pending || r.pieceId !== pending.pieceId).concat(pending ? [pending] : []);
      const saved = await this.call('/save', { reserves, likes: picks.likes, reasons: picks.reasons, likeColors: picks.likeColors || {} });
      if (!saved.ok) {
        if (saved.reason === 'signed_out') this.sheetFor = { ...this.sheetFor, kind: 'phone' };
        if (saved.reason === 'signed_out') this.drawSheet();
        this.error(saved.reason);
        return false;
      }
      kept(() => localStorage.removeItem(PICKS));
      const skipped = (saved.skipped || []).map((one) => (typeof one === 'string' ? one : one?.pieceId));
      this.skipped = skipped.length;
      track('Room Picks Saved', { step: this.saveStep || 'first_reserve', reserved: reserves.filter((r) => !skipped.includes(r.pieceId)).length, liked: picks.likes.length, skipped: skipped.length });
      this.saveStep = null;
      await this.call('/consent', { whatsapp: true });
      const me = await this.call('/me');
      this.local = false;
      if (me.ok) this.me = me;
      else if (saved.mine) this.me.mine = saved.mine;
      for (const reserve of reserves) {
        if (skipped.includes(reserve.pieceId)) continue;
        const piece = this.me.pieces.find((p) => p.id === reserve.pieceId);
        if (piece) track('Size Reserved', { piece_id: piece.id, piece_title: piece.title, color: reserve.color || undefined, size: reserve.size || undefined, ...this.where(piece.id) });
      }
      this.pendingReserve = null;
      // What shows behind the sheet takes his saved reserves at once.
      // (The piece page and My list are drawn again once the confirmation is closed: drawing now would close it.)
      if (this.view.querySelector('[data-room-feed], .room__grid, .room__rail')) this.paint();
      else this.refreshOnClose = true;
      return true;
    }

    /** A short line at the foot of the page that something was done ("Reserved"), for a second. */
    toast(text) {
      if (!this.toastLine) {
        this.toastLine = document.createElement('p');
        this.toastLine.className = 'room__toast';
        this.toastLine.setAttribute('role', 'status');
        this.append(this.toastLine);
      }
      window.clearTimeout(this.toastTimer);
      this.toastLine.textContent = text;
      this.toastLine.hidden = false;
      this.toastTimer = window.setTimeout(() => { this.toastLine.hidden = true; }, 1400);
    }

    /** "Save my reserves": his phone, then the WhatsApp code. Only while he is not signed in. */
    saveForm() {
      return `
        <form class="room__form room__save" novalidate data-room-form="save">
          ${this.unsaved ? `<p class="room__unsaved">${esc(this.t('save.not_saved'))}</p>` : ''}
          <p class="room__text">${esc(this.t('save.why'))}</p>
          <label class="room__label" for="RoomNumber">${esc(this.t('save.phone_label'))}</label>
          <div class="room__phone" dir="ltr">
            <select class="room__input room__code" name="code" aria-label="${esc(this.t('save.phone_label'))}" autocomplete="tel-country-code">
              ${CODES.map(([c]) => `<option value="${c}"${(this.pending?.code || '965') === c ? ' selected' : ''}>+${c}</option>`).join('')}
            </select>
            <input class="room__input" id="RoomNumber" name="number" type="tel" inputmode="tel" autocomplete="tel-national" required aria-describedby="RoomError" value="${esc(this.pending?.number || '')}">
          </div>
          <p class="room__error" id="RoomError" role="alert" data-room-error></p>
          <button class="button button--full room__button" type="submit">${esc(this.t('save.button'))}</button>
          <p class="room__fine">${esc(this.t('gate.privacy'))}</p>
        </form>`;
    }

    done(focus) {
      const { reserved, liked } = this.counts();
      const arriving = this.screen !== 'done';
      const date = this.date(this.me.closesAt);
      const saved = !this.local && this.justSaved ? `<p class="room__saved" role="status">${esc(this.t('save.saved'))}</p>` : '';
      const skipped = !this.local && this.skipped ? `<p class="room__unsaved" role="status">${esc(this.t('save.skipped'))}</p>` : '';
      this.show(`
        <div class="room__screen page-width">
          <span class="room__tick" aria-hidden="true"></span>
          <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(this.t('done.heading', { reserved: this.num(reserved), liked: this.num(liked) }))}</h1>
          ${saved}${skipped}
          ${date ? `<p class="room__text">${esc(this.t('done.text', { date }))}</p>` : ''}
          ${this.unsaved && this.requestsOpen ? `<p class="room__unsaved">${esc(this.t('save.not_saved'))}</p><button class="button button--full room__button" type="button" data-room-action="save-old">${esc(this.t('save.button'))}</button>` : ''}
          <p class="room__eyebrow">${esc(this.t('done.next_title'))}</p>
          <ol class="room__rules">
            ${[this.t('done.next_1', { date: date || '' }), this.t('done.next_2'), this.t('done.next_3')].map((text, at) => `<li><span class="room__rule-number">${this.num(at + 1)}</span><span>${esc(text)}</span></li>`).join('')}
          </ol>
          ${this.local ? '' : `
            <p class="room__eyebrow">${esc(this.t('done.message_title'))}</p>
            <label class="room__switch">
              <span>${esc(this.t('done.whatsapp'))}</span>
              <input type="checkbox" role="switch" data-room-consent="whatsapp"${this.me.consent?.whatsapp ? ' checked' : ''}>
            </label>`}
          <div class="room__actions">
            <button class="button button--secondary room__button" type="button" data-room-action="invite">${esc(this.t('done.invite'))}</button>
            <a class="button room__button" href="#pieces" data-room-action="back">${esc(this.t('done.see'))}</a>
          </div>
        </div>`, { focus });
      this.screen = 'done';
      if (arriving) track('Room Done Opened', { reserved, liked, saved: !this.local });
    }

    results(focus) {
      const mine = this.me.mine || {};
      const reserves = mine.reserves || [];
      const likes = mine.likes || [];
      const ours = (this.me.pieces || []).filter((p) => likes.includes(p.id) || reserves.some((r) => r.pieceId === p.id));
      const outcome = this.me.results;
      const rows = outcome ? ours.map((p) => {
        const reserve = reserves.find((r) => r.pieceId === p.id);
        const coming = outcome[p.id] === 'coming';
        const line = coming ? (reserve ? this.t('results.reserved', { size: this.sizeText(reserve) }) : this.t('results.liked')) : this.t('results.not_coming');
        const image = p.images?.[0];
        return `<li class="room__result">
          ${image ? `<img src="${esc(this.sized(image.url, 200))}" alt="" width="200" height="250" loading="lazy">` : '<span></span>'}
          <span class="room__result-text"><b>${esc(p.title)}</b><span>${esc(line)}</span></span>
          <span class="room__result-mark${coming ? ' is-coming' : ''}">${esc(coming ? this.t('results.coming') : this.t('results.not_this_time'))}</span>
        </li>`;
      }).join('') : '';
      track('Room Closed Seen');
      this.show(`
        <div class="room__screen page-width">
          <p class="room__eyebrow">${esc(this.t('results.eyebrow'))}</p>
          <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(outcome ? this.t('results.heading') : this.t('state.closed_heading'))}</h1>
          ${outcome ? `<ul class="room__results" role="list">${rows}</ul><p class="room__fine">${esc(this.t('results.footer'))}</p>` : `<p class="room__text">${esc(this.t('results.waiting'))}</p>`}
          <a class="button button--full room__button" href="${esc(this.dataset.shopUrl)}">${esc(this.t('results.shop'))}</a>
        </div>`, { focus });
    }

    closed() {
      track('Room Closed Seen');
      this.message(this.t('state.closed_heading'), this.t('state.closed_text'));
    }

    /** Before it opens: the campaign photo, a live "Opens in …" and the date, as in the app. When it opens, the pieces come by themselves. */
    notOpen(iso) {
      const date = this.date(iso);
      const words = this.querySelector('[data-countdown-words]')?.textContent;
      const opens = Date.parse(iso || '');
      const countdown = words && opens > Date.now() ? `<kz-countdown class="room__countdown" data-from="${esc(new Date(opens).toISOString())}" data-to="${esc(new Date(Date.parse(this.room?.closesAt || '') || opens + 864e5).toISOString())}" data-keep role="timer" hidden><span data-countdown-text></span><script type="application/json">${words}</script></kz-countdown>` : '';
      const image = this.campaign();
      this.show(`
        <div class="room__screen room__screen--gate page-width${image ? ' has-image' : ''}">
          ${image}
          <div class="room__gate-text">
            ${this.kicker('eyebrow')}
            <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(this.t('state.not_open_heading'))}</h1>
            ${countdown}
            ${date ? `<p class="room__text">${esc(this.t('state.not_open_text', { date }))}</p>` : ''}
            <a class="button button--secondary room__button" href="${esc(this.dataset.homeUrl || '/')}">${esc(this.t('state.home'))}</a>
          </div>
        </div>`, { dark: true, focus: false });
      if (!this.opensWatch) {
        this.opensWatch = true;
        this.addEventListener('countdown:opened', () => this.start());
      }
    }

    nothing() {
      this.message(this.t('state.off_heading'), '');
    }

    message(heading, text) {
      this.show(`
        <div class="room__screen page-width">
          <p class="room__eyebrow">${esc(this.t('eyebrow'))}</p>
          <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(heading)}</h1>
          ${text ? `<p class="room__text">${esc(text)}</p>` : ''}
          <a class="button button--secondary room__button" href="${esc(this.dataset.homeUrl || '/')}">${esc(this.t('state.home'))}</a>
        </div>`, { focus: false });
    }

    trouble(reason, again) {
      // Pressed again and still failing: the line is said again, so the press is never met with nothing.
      if (this.retry && this.querySelector('[data-room-action="retry"]')) this.flash(reason);
      this.retry = again;
      this.show(`
        <div class="room__screen page-width">
          <p class="room__eyebrow">${esc(this.t('eyebrow'))}</p>
          <h1 class="room__heading" tabindex="-1" data-room-heading>${esc(this.t(`error.${reason}`) || this.t('error.unavailable'))}</h1>
          <button class="button button--secondary room__button" type="button" data-room-action="retry">${esc(this.t('code.continue'))}</button>
        </div>`, { focus: false });
    }

    heart() {
      return '<svg class="room__heart" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/></svg>';
    }

    /** One column (one by one) or a 2×2 grid. */
    viewIcon(mode) {
      const shapes = mode === 'reel' ? '<rect x="6" y="3.5" width="12" height="17"/>' : '<rect x="4" y="4" width="7" height="7"/><rect x="13" y="4" width="7" height="7"/><rect x="4" y="13" width="7" height="7"/><rect x="13" y="13" width="7" height="7"/>';
      return `<svg class="room__view-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${shapes}</svg>`;
    }

    cross() {
      return '<svg class="room__cross" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    }

    // Points to the next photo; mirrored for the previous one and on Arabic pages (CSS).
    chevron() {
      return '<svg class="room__chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>';
    }

    sized(url, width) {
      return kept(() => { const at = new URL(url, location.href); at.searchParams.set('width', width); return at.toString(); }, url);
    }

    srcset(url) {
      return [400, 600, 900, 1200].map((w) => `${this.sized(url, w)} ${w}w`).join(', ');
    }

    /** A refusal, in words: on the screen's own line when it has one, else in the line at the foot of the page. */
    error(reason, values = {}) {
      const text = this.t(`error.${reason}`, values) || this.t('error.unavailable');
      const place = this.errorPlace();
      if (!place) return this.flash(reason, values);
      place.textContent = '';
      // Written a beat later, so the same refusal twice in a row is still read out and seen to change.
      window.setTimeout(() => { place.textContent = text; }, 60);
    }

    /** Where a refusal is written: in the open sheet, where he is looking, else on the screen. */
    errorPlace() {
      return (this.sheet.open && this.sheet.querySelector('[data-room-error]')) || this.querySelector('[data-room-view] [data-room-error]');
    }

    /** The line at the foot of the page, for a few seconds. */
    flash(reason, values = {}) {
      const text = this.t(`error.${reason}`, values) || this.t('error.unavailable');
      window.clearTimeout(this.flashTimer);
      this.flashLine.textContent = text;
      this.flashLine.hidden = false;
      this.flashTimer = window.setTimeout(() => { this.flashLine.hidden = true; }, 6000);
    }

    // ─── What he does ────────────────────────────────────────────────────────

    /** Photos full screen in the product page's viewer (pinch and double-tap to zoom), from the one tapped. */
    zoomInto(openers, from) {
      const viewer = this.querySelector('product-viewer');
      if (!viewer?.open || !openers.length) return;
      const items = openers.map((opener) => ({ slide: opener.closest('li') || opener, opener, template: null, thumb: opener.querySelector('img') }));
      this.viewing = true;
      viewer.querySelector('dialog')?.addEventListener('close', () => window.setTimeout(() => { this.viewing = false; }, 600), { once: true });
      viewer.open(items, Math.max(0, openers.indexOf(from)));
    }

    /** Two fingers on a photo of the feed: it opens full screen to be pinched, and the page itself never zooms. */
    onTouch(event) {
      if (event.touches.length !== 2) return;
      const photo = event.target.closest?.('.room__reel-photo');
      if (!photo) return;
      event.preventDefault();
      window.clearTimeout(this.lastTap?.timer);
      this.lastTap = null;
      this.zoomInto([...photo.parentElement.children], photo);
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-room-form]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button.getAttribute('aria-busy') === 'true') return;
      if (form.dataset.roomForm === 'save') {
        const phone = e164(form.code.value, form.number.value);
        this.pending = { code: form.code.value, number: form.number.value };
        // Where the number was asked, for the save steps in Mixpanel (never the number itself).
        this.saveStep = this.sheet.open && this.sheetFor?.kind === 'phone' ? (this.sheetFor.old ? 'old_picks' : 'first_reserve') : 'done';
        const submitted = (more) => track('Room Phone Submitted', { step: this.saveStep, country_code: form.code.value, ...more });
        if (!phone) {
          submitted({ code_sent: false, reason: 'invalid_phone' });
          return this.error('invalid_phone');
        }
        button.setAttribute('aria-busy', 'true');
        const answer = await this.call('/code', { phone, locale: this.ar ? 'ar' : 'en' });
        button.removeAttribute('aria-busy');
        submitted(answer.ok ? { code_sent: true } : { code_sent: false, reason: answer.reason });
        if (!answer.ok) {
          if (['not_open', 'closed', 'off'].includes(answer.reason)) return this.start();
          return this.error(answer.reason);
        }
        Object.assign(this.pending, { phone, resendAfter: answer.resendAfter });
        if (this.sheet.open && this.sheetFor?.kind === 'phone') {
          this.sheetFor = { ...this.sheetFor, kind: 'code' };
          this.drawSheet();
          this.sheet.querySelector('#RoomOtp')?.focus();
          return this.countdown(this.pending.resendAfter || 60);
        }
        return this.codeScreen();
      }
      if (form.dataset.roomForm === 'verify') {
        const code = form.otp.value.replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)).replace(/\D/g, '');
        if (code.length !== 6) return this.error('invalid');
        button.setAttribute('aria-busy', 'true');
        const answer = await this.call('/verify', { phone: this.pending.phone, code });
        button.removeAttribute('aria-busy');
        if (!answer.ok) return this.error(answer.reason, { tries: this.num(answer.attemptsLeft ?? '') });
        this.token = answer.token;
        kept(() => localStorage.setItem(TOKEN, answer.token));
        track('Room Code Verified', { step: this.saveStep || 'done' });
        this.justSaved = true;
        // From the number sheet: this reserve (and everything picked so far) is saved now, then the confirmation.
        if (this.sheet.open && this.sheetFor?.kind === 'code') {
          button.setAttribute('aria-busy', 'true');
          const done = await this.saveNumbered();
          button.removeAttribute('aria-busy');
          if (!done) return;
          this.sheetFor = { ...this.sheetFor, kind: 'confirm' };
          this.drawSheet();
          return this.sheet.querySelector('[data-room-sheet-first]')?.focus();
        }
        if (location.hash !== '#done') history.replaceState(null, '', '#done');
        return this.loadMe(true);
      }
    }

    async onClick(event) {
      const target = event.target.closest('[data-room-action]');
      if (!target) return;
      const action = target.dataset.roomAction;
      const id = target.dataset.id;
      const piece = id && this.me?.pieces.find((p) => p.id === id);
      if (['open', 'done', 'back', 'explore'].includes(action)) event.preventDefault();
      if (action === 'explore') return this.go('#pieces');
      // In the home row a card opens the piece on the pre-order page, in the colour picked here (kept in kz_room_colors).
      if (action === 'open' && this.rail) {
        track('Room Tile Tapped', { placement: 'home_rail', piece_id: id, piece_title: piece?.title }, { leaving: true });
        window.location.href = `${this.dataset.pageUrl || ''}#piece=${encodeURIComponent(id)}`;
        return;
      }
      if (action === 'open') return this.go(`#piece=${encodeURIComponent(id)}`);
      if (action === 'done') return this.go('#done');
      // Back to the pieces: one step back in the history when he came from them, so the back button stays right.
      if (action === 'back') return this.depth ? history.back() : this.go('#pieces');
      if (action === 'retry') {
        target.setAttribute('aria-busy', 'true');
        return this.retry?.();
      }
      if (action === 'change-number') {
        if (this.sheet.open) {
          this.sheetFor = { ...this.sheetFor, kind: 'phone' };
          this.drawSheet();
          return this.sheet.querySelector('#RoomNumber')?.focus();
        }
        return this.route(true);
      }
      if (action === 'save-old') return this.askNumber(null, true);
      if (action === 'resend') {
        const answer = await this.call('/code', { phone: this.pending.phone, locale: this.ar ? 'ar' : 'en' });
        if (!answer.ok) return this.error(answer.reason);
        return this.countdown(answer.resendAfter || 60);
      }
      if (action === 'filter') {
        this.filter = target.dataset.type;
        this.feedTop = 0;
        this.gridTop = 0;
        return this.grid(false);
      }
      if (action === 'view') {
        const mode = target.dataset.mode;
        if (mode === this.mode) return;
        this.mode = mode;
        this.feedTop = 0;
        this.gridTop = 0;
        this.hint = false;
        kept(() => sessionStorage.setItem(VIEW, mode));
        kept(() => localStorage.setItem(HINT, JSON.stringify({ used: true })));
        track('Room View Changed', { view_mode: mode });
        this.grid(false);
        return this.view.querySelector('[data-room-action="view"]')?.focus();
      }
      if (action === 'close-sheet') return this.sheet.close();
      // A photo of the piece page opens full screen, with pinch and double-tap to zoom: the product page's own viewer (assets/product-viewer.js).
      if (action === 'zoom') return this.zoomInto([...this.view.querySelectorAll('.room__zoom')], target);
      // A photo in the feed: one tap opens the piece, a double tap opens it full screen (Ibrahim, 12:38 am).
      if (action === 'reel-photo') {
        event.preventDefault();
        if (this.lastTap?.target === target && Date.now() - this.lastTap.at < 320) {
          window.clearTimeout(this.lastTap.timer);
          this.lastTap = null;
          return this.zoomInto([...target.parentElement.children], target);
        }
        window.clearTimeout(this.lastTap?.timer);
        this.lastTap = { target, at: Date.now(), timer: window.setTimeout(() => { this.lastTap = null; this.go(`#piece=${encodeURIComponent(id)}`); }, 320) };
        return;
      }
      if (action === 'photo') {
        const strip = target.parentElement.querySelector('[data-room-photos]');
        const step = strip.firstElementChild.getBoundingClientRect().width;
        const rtl = getComputedStyle(strip).direction === 'rtl';
        return strip.scrollBy({ left: step * Number(target.dataset.step) * (rtl ? -1 : 1), behavior: 'smooth' });
      }
      if (action === 'choose') {
        (this.choice[id] ||= {})[target.dataset.kind] = target.dataset.value;
        if (target.dataset.kind === 'color') this.remember(id, target.dataset.value);
        if (this.sheet.open) {
          if (target.dataset.kind === 'color') this.recolor(piece);
          return this.drawSheet(`[data-room-action="choose"][data-kind="${target.dataset.kind}"][data-value="${CSS.escape(target.dataset.value)}"]`);
        }
        return this.piece(piece, false);
      }
      if (action === 'shade') {
        if (target.getAttribute('aria-checked') === 'true') return;
        this.choiceFor(piece, (this.me.mine?.reserves || []).find((r) => r.pieceId === id)).color = target.dataset.value;
        this.remember(id, target.dataset.value);
        this.recolor(piece);
        return track('Piece Colour Chosen', { piece_id: id, piece_title: piece.title, color: target.dataset.value, ...this.where(id) });
      }
      if (action === 'invite') return this.invite();
      if (!piece || target.getAttribute('aria-busy') === 'true') return;
      const about = () => ({ piece_id: id, piece_title: piece.title, ...this.where(id) });
      if (action === 'sheet') return this.openSheet('reserve', piece);
      if (action === 'reasons') return this.openSheet('reasons', piece);
      if (action === 'reserve') {
        if (target.getAttribute('aria-disabled') === 'true') return;
        const choice = this.choice[id] || {};
        if ((piece.colors || []).length > 1 && !choice.color) return this.ask('color', this.t('piece.pick_colour'));
        if ((piece.sizes || []).length > 1 && !choice.size) return this.ask('size', this.t('piece.pick_size'));
        // Not signed in yet: the first reserve asks his WhatsApp number (Ibrahim, 28 Oct). After that, every reserve saves at once.
        // With the setting off, the reserve is kept on this phone, as before 28 Oct.
        if (this.local && this.asksNumber) return this.askNumber({ pieceId: id, color: choice.color || null, size: choice.size || null });
        return this.change(target, '/reserve', { pieceId: id, color: choice.color || null, size: choice.size || null }, () => {
          // kept_on_phone: saved in this browser only (number step off); it is counted again when saved with a number.
          track('Size Reserved', { ...about(), color: choice.color || undefined, size: choice.size || undefined, kept_on_phone: this.local || undefined });
          this.toast(this.t('piece.reserved_toast'));
        });
      }
      // (The colour and size picked stay picked: reserving again is one tap.)
      if (action === 'unreserve') {
        return this.change(target, '/unreserve', { pieceId: id }, () => track('Reserve Cancelled', about()));
      }
      if (action === 'like') {
        const liked = !(this.me.mine?.likes || []).includes(id);
        // The colour on screen goes with the like (the server keeps one like per piece, in that colour).
        const color = (piece.colors || []).length ? this.colorOf(piece) : null;
        return this.change(target, '/like', { pieceId: id, liked, color }, () => (liked ? track('Piece Liked', { ...about(), color: color || undefined }) : track('Piece Unliked', about())));
      }
      if (action === 'reason') {
        const reason = this.me.mine?.reasons?.[id] === target.dataset.reason ? null : target.dataset.reason;
        return this.change(target, '/reason', { pieceId: id, reason }, () => reason && track('Not For Me', { ...about(), reason }));
      }
    }

    /**
     * Reserve pressed with no colour or size picked: the same cue as "Add to bag" on a product page
     * (assets/product.js Kanze.shake: the row gives one short shake, none with "reduce motion"),
     * and the line under the buttons says what to pick, in the sale colour.
     */
    ask(kind, text) {
      const row = (this.sheet.open ? this.sheet : this.view).querySelector(`[data-room-options="${kind}"]`);
      if (row) {
        if (window.Kanze?.shake) window.Kanze.shake(row);
        else row.scrollIntoView({ block: 'center' });
      }
      const place = this.errorPlace();
      if (place) {
        place.textContent = text;
        place.classList.add('is-asking');
      }
      this.say(text);
    }

    /** One change: kept in this browser before he saves, else on the server. The screen is drawn again from the result. */
    async change(button, path, body, done) {
      if (this.local) {
        const mine = this.me.mine;
        const id = body.pieceId;
        if (path === '/reserve') mine.reserves = mine.reserves.filter((r) => r.pieceId !== id).concat({ pieceId: id, color: body.color, size: body.size });
        if (path === '/unreserve') mine.reserves = mine.reserves.filter((r) => r.pieceId !== id);
        if (path === '/like') {
          mine.likes = body.liked ? [...new Set([...mine.likes, id])] : mine.likes.filter((liked) => liked !== id);
          mine.likeColors = { ...(mine.likeColors || {}) };
          if (body.liked) mine.likeColors[id] = body.color ?? null;
          else delete mine.likeColors[id];
        }
        if (path === '/reason') { if (body.reason) mine.reasons[id] = body.reason; else delete mine.reasons[id]; }
        this.keepPicks(mine);
      } else {
        button.setAttribute('aria-busy', 'true');
        const answer = await this.call(path, body);
        button.removeAttribute('aria-busy');
        if (!answer.ok) {
          if (answer.reason === 'signed_out') return this.rail ? this.startRail() : this.loadPieces(true);
          if (['paused', 'closed', 'not_open', 'in_store'].includes(answer.reason)) {
            if (answer.reason === 'paused' && this.me) this.me.requestsOpen = false;
            return this.rail ? this.startRail() : this.loadMe(false);
          }
          return this.error(answer.reason);
        }
        if (answer.mine) this.me.mine = answer.mine;
      }
      done?.();
      const { reserved, liked } = this.counts();
      this.say(this.t('room.bar', { reserved: this.num(reserved), liked: this.num(liked) }));
      // A reserve or a reason given in a sheet closes it; on the pieces the buttons change in place, so the feed does not move.
      const opener = this.sheet.open && this.view.querySelector(`[data-room-action="${this.sheetFor.kind === 'reasons' ? 'reasons' : 'sheet'}"][data-id="${CSS.escape(body.pieceId)}"]`);
      if (this.sheet.open && path !== '/like') this.sheet.close();
      if (this.view.querySelector('[data-room-feed], .room__grid, .room__rail')) {
        this.paint();
        if (opener && !this.sheet.open) opener.focus({ preventScroll: true });
        return;
      }
      this.route(false);
    }

    async onChange(event) {
      const box = event.target.closest('[data-room-consent]');
      if (!box) return;
      const answer = await this.call('/consent', { whatsapp: box.checked });
      if (!answer.ok) {
        box.checked = !box.checked;
        return this.flash(answer.reason);
      }
      if (answer.consent) this.me.consent = answer.consent;
    }

    async invite() {
      const url = location.origin + location.pathname + (location.search.includes('view=') ? location.search : '');
      if (navigator.share) {
        await navigator.share({ title: this.t('eyebrow'), text: this.t('done.invite_text'), url }).catch(() => {});
        return;
      }
      await navigator.clipboard?.writeText(url).catch(() => {});
      this.say(this.t('done.copied'));
    }
  }

  if (!customElements.get('selection-room')) customElements.define('selection-room', SelectionRoom);
})();
